import React, { useEffect, useState } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { Box, Button, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import * as EmployeeService from 'services/EmployeeService';
import { EmployeeWithUserInfo } from 'utils/interfaces';
import { isAdmin as checkIsAdmin } from 'utils/auth-utils';
import SignatureGate from './signature/SignatureGate';

interface PrivateRouteProps {
  component: React.ComponentType<any>;
  [key: string]: any;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ component: Component, ...rest }) => {
  const { keycloak } = useKeycloak();
  const isKeycloakEnabled = process.env.REACT_APP_KEYCLOAK_ENABLED === 'true';
  const isAuthenticated = isKeycloakEnabled ? keycloak.authenticated : true;
  const admin = isKeycloakEnabled ? checkIsAdmin(keycloak) : false;

  const [profile, setProfile] = useState<EmployeeWithUserInfo | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  // Distinct from "profile is null because it hasn't loaded yet": a transient failure (backend
  // hiccup, timeout) must never be treated the same as "no signature needed" — profile === null
  // used to mean both, which silently let an employee through to the dashboard whenever the
  // profile fetch merely failed, skipping the mandatory signature check entirely.
  const [profileFetchFailed, setProfileFetchFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || admin) {
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);

    EmployeeService.getMyProfile(keycloak.token!, () => keycloak.logout()).then(data => {
      if (!cancelled) {
        setProfile(data ?? null);
        setProfileFetchFailed(!data);
        setProfileLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, admin, keycloak.token, retryCount]);

  return (
    <Route
      {...rest}
      render={props => {
        if (!isAuthenticated) {
          return <Redirect to="/auth" />;
        }

        if (!admin && rest.path === '/admin') {
          return <Redirect to="/dipendente/interventi" />;
        }

        if (admin && rest.path === '/dipendente') {
          return <Redirect to="/admin/home" />;
        }

        if (!admin && profileLoading) {
          return (
            <Flex justify="center" align="center" height="100vh">
              <Spinner size="xl" color="primary" />
            </Flex>
          );
        }

        // Fail closed: a profile fetch failure (backend hiccup, timeout — not a 401, that already
        // logs the user out above) must never fall through to the dashboard, since that would skip
        // the mandatory signature check below whenever the fetch merely failed to complete.
        if (!admin && profileFetchFailed) {
          return (
            <Center minH="100vh" bg="gray.50">
              <Box bg="white" borderRadius="20px" boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.18)" p={8} textAlign="center">
                <Text fontSize="lg" color="red.500" mb={4}>
                  Impossibile verificare il tuo profilo. Riprova.
                </Text>
                <Button
                  onClick={() => setRetryCount(c => c + 1)}
                  _hover={{ bg: 'gray.700' }}
                  _active={{ bg: 'gray.600' }}
                  _focus={{ boxShadow: 'none' }}
                  backgroundColor="#688CC8"
                  color="white"
                  height="36px"
                  borderRadius="6px"
                >
                  Riprova
                </Button>
              </Box>
            </Center>
          );
        }

        if (!admin && profile && !profile.hasSignature) {
          return (
            <SignatureGate
              employeeId={profile.id}
              employeeName={`${profile.firstName} ${profile.lastName}`}
              onSignatureComplete={() => setProfile(prev => (prev ? { ...prev, hasSignature: true } : prev))}
            />
          );
        }

        return <Component {...props} />;
      }}
    />
  );
};

export default PrivateRoute;